import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, CircularProgress, Collapse } from '@mui/material';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { Maybe } from 'types/types';

type ConnectorErrorListUnifiedProps = {
  errors: any;
  retryURL?: Maybe<string>;
};

const useStyles = makeStyles((theme) => ({
  itemContainer: {
    minWidth: '500px',
    paddingLeft: theme.spacing(4.5),
    paddingBottom: theme.spacing(0),
    margin: theme.spacing(0, 1)
  },
  listContainer: {
    maxHeight: '240px',
    padding: theme.spacing(0, 2, 1.5, 2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    marginBottom: theme.spacing(1.5)
  },
  errorContainer: {
    display: 'flex',
    color: theme.palette.error.dark,
    padding: theme.spacing(1.3, 2, 0.5, 2),
    margin: theme.spacing(2, 0, 0, 0),
    backgroundColor: theme.palette.error.light,
    alignItems: 'center'
  },
  retryButtonContainer: {
    flexShrink: 0,
    alignItems: 'center'
  },
  retryButton: {
    minWidth: 'fit-content',
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    padding: '0px',
    '&:hover': {
      backgroundColor: theme.palette.error.light
    },
    ...theme.typography.caption
  },
  retryIndicator: {
    verticalAlign: 'middle',
    marginLeft: theme.spacing(1),
    color: theme.palette.error.dark
  },
  outerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  title: {
    marginTop: theme.spacing(1)
  },
  dataValue: {
    display: 'block'
  },
  icon: {
    color: theme.palette.common.black
  },
  li: {
    display: 'flex',
    alignItems: 'center'
  }
}));

export const ConnectorErrorListUnified: React.FC<ConnectorErrorListUnifiedProps> = ({
  errors,
  retryURL
}: ConnectorErrorListUnifiedProps) => {
  const [open, setOpen] = React.useState(true);
  const handleClick = () => {
    setOpen(!open);
  };
  const data = {
    collectorSetting: { item: { installStatus: { errors: [] } } }
  }; // graph-ql data
  const loading = null; // graph-ql data
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [collectorErrors, setCollectorErrors] = useState<
    Array<any | null | undefined> | undefined | null
  >(errors);

  useEffect(() => {
    if (!loading && data?.collectorSetting?.item?.installStatus?.errors) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const errors = data.collectorSetting.item.installStatus.errors;
    }
  }, [
    data?.collectorSetting?.item?.installStatus?.errors,
    setIsRetrying,
    setCollectorErrors,
    loading
  ]);

  const classes = useStyles();

  if (!collectorErrors || collectorErrors.length < 1) {
    return null;
  }

  return (
    <div
      className={classes.outerContainer}
      data-testid="setting-dialog-CollectorErrorListUnified"
    >
      {collectorErrors && collectorErrors.length > 0 && (
        <>
          <div className={classes.errorContainer} onClick={handleClick}>
            {open ? <ExpandLess className={classes.icon} /> : <ExpandMore />}
            <Typography data-testid="errorBanner" align="left" variant="body2">
              <FormattedMessage
                id={'collectorSetting.errors'}
                values={{ number: collectorErrors.length }}
              />
            </Typography>
          </div>
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
            className={classnames(classes.listContainer)}
          >
            <div>
              {collectorErrors.map((data, index) => {
                return (
                  <ul key={index} className={classes.itemContainer}>
                    <li>
                      <div className={classes.li}>
                        <Typography
                          data-testid="errorMessage"
                          variant="caption"
                          className={classes.dataValue}
                        >
                          {data?.message}
                        </Typography>
                        {retryURL && (
                          <span className={classes.retryButtonContainer}>
                            <span>&nbsp; - </span>
                            {isRetrying && (
                              <CircularProgress
                                data-testid="progressIndicator"
                                className={classes.retryIndicator}
                                size={14}
                              />
                            )}
                          </span>
                        )}
                      </div>
                    </li>
                  </ul>
                );
              })}
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
};
